import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function CharterInquiry() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const aircraftId = params.get("aircraft");
  const source = params.get("src") || "direct";

  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    departure_airport: "", arrival_airport: "",
    departure_date: "", passengers: 2, trip_type: "One-way",
    message: "", aircraft_id: aircraftId || undefined, source,
  });
  const [done, setDone] = useState(false);
  const [aircraft, setAircraft] = useState(null);

  useEffect(() => {
    if (aircraftId) api.get(`/fleet/${aircraftId}`).then(r => setAircraft(r.data)).catch(() => {});
  }, [aircraftId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/charter/inquiries", form);
      setDone(true);
      toast.success("Your request has been received. An ops lead will reply within 4 hours.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    }
  };

  const inp = "w-full bg-[#FAF7F0] border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-4 py-3 rounded-sm text-[#1A1D24] transition-colors";

  return (
    <div className="min-h-screen bg-[#FAF7F0] text-[#1A1D24]">
      <PublicNav />
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-14">
        <div className="as-overline mb-4">Charter Inquiry</div>
        <h1 className="as-serif text-5xl md:text-6xl font-light leading-[1]" data-testid="inquiry-title">Tell us the mission.</h1>
        <p className="text-[#756E64] mt-4 max-w-xl font-light">You'll receive a tailored quote — with aircraft options, catering, ground handling and concierge line-items — within 4 hours.</p>

        {aircraft && (
          <div className="as-card p-5 mt-8 flex items-center gap-5" data-testid="inquiry-aircraft-badge">
            <img src={aircraft.image_url} className="w-24 h-16 object-cover rounded-sm"/>
            <div>
              <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Requesting Quote For</div>
              <div className="as-serif text-2xl">{aircraft.manufacturer} {aircraft.model}</div>
            </div>
          </div>
        )}

        {done ? (
          <div className="as-card p-10 mt-10 text-center" data-testid="inquiry-success">
            <CheckCircle2 size={36} className="as-gold mx-auto"/>
            <div className="as-serif text-3xl mt-4">Received.</div>
            <p className="text-[#756E64] mt-3">Your dedicated ops lead will reach out within 4 hours at {form.email}.</p>
            <button onClick={() => nav("/charter")} className="as-btn-ghost mt-8 px-5 py-3 rounded-sm as-mono text-xs uppercase tracking-widest" data-testid="inquiry-back">Back to fleet</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="inquiry-form">
            <Field label="Full name" required>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inp} data-testid="inquiry-name"/>
            </Field>
            <Field label="Email" required>
              <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inp} data-testid="inquiry-email"/>
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inp} data-testid="inquiry-phone"/>
            </Field>
            <Field label="Company / Family Office">
              <input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className={inp} data-testid="inquiry-company"/>
            </Field>
            <Field label="Departure Airport (ICAO/IATA)" required>
              <input required value={form.departure_airport} onChange={e => setForm({...form, departure_airport: e.target.value.toUpperCase()})} className={inp} placeholder="KTEB" data-testid="inquiry-departure"/>
            </Field>
            <Field label="Arrival Airport (ICAO/IATA)" required>
              <input required value={form.arrival_airport} onChange={e => setForm({...form, arrival_airport: e.target.value.toUpperCase()})} className={inp} placeholder="LFPB" data-testid="inquiry-arrival"/>
            </Field>
            <Field label="Departure Date" required>
              <input required type="date" value={form.departure_date} onChange={e => setForm({...form, departure_date: e.target.value})} className={inp} data-testid="inquiry-date"/>
            </Field>
            <Field label="Passengers" required>
              <input required type="number" min="1" value={form.passengers} onChange={e => setForm({...form, passengers: parseInt(e.target.value)})} className={inp} data-testid="inquiry-passengers"/>
            </Field>
            <Field label="Trip Type">
              <select value={form.trip_type} onChange={e => setForm({...form, trip_type: e.target.value})} className={inp} data-testid="inquiry-trip-type">
                <option>One-way</option>
                <option>Round trip</option>
                <option>Multi-leg</option>
              </select>
            </Field>
            <Field label="Source">
              <select value={form.source} onChange={e => setForm({...form, source: e.target.value})} className={inp} data-testid="inquiry-source">
                <option value="direct">Direct</option>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="referral">Referral</option>
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Anything we should know?">
                <textarea rows="4" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className={inp} placeholder="Catering, security, ground transport, discretion requirements…" data-testid="inquiry-message"/>
              </Field>
            </div>
            <div className="md:col-span-2 flex items-center justify-between pt-4">
              <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">Response SLA · ≤ 4 hours</div>
              <button className="as-btn-gold px-8 py-4 rounded-sm as-mono text-xs uppercase tracking-[0.2em]" data-testid="inquiry-submit">Submit Inquiry</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mb-2">{label}{required && <span className="as-gold ml-1">*</span>}</div>
      {children}
    </label>
  );
}
